import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  IconLinkOff,
  IconUserPlus,
  IconAlertCircle,
  IconEye,
  IconEyeOff,
  IconCheck,
} from "@tabler/icons-react";
import styles from "./acceptInvite.module.css";
import { getTeamApiError } from "../../../components/TeamManagement/utils/apiError";
import { clientTeamAcceptInvite } from "../../../services/clientCalls";
import Button from "../../../components/UI/Button/button";
import { Logo } from "../../../constants";

const ALREADY_ACCEPTED =
  "This invite has already been accepted. Please log in.";
const LINK_INVALID_EXPIRED = "This invite link is invalid or has expired";
const LINK_SUPERSEDED =
  "This invite link is no longer valid. Please ask an admin to resend your invite.";
const ORG_INACTIVE = "This organisation's account is no longer active";

const DEAD_LINK_MESSAGES = new Set([
  ALREADY_ACCEPTED,
  LINK_INVALID_EXPIRED,
  LINK_SUPERSEDED,
  ORG_INACTIVE,
]);

function deadStateCopy(message) {
  if (message === ALREADY_ACCEPTED) {
    return {
      title: "You've already accepted this invite",
      body: (
        <>
          Please <Link to="/login">log in</Link> instead.
        </>
      ),
    };
  }
  if (message === ORG_INACTIVE) {
    return {
      title: "This organisation's account is no longer active",
      body: "There's nothing to do here — reach out to your admin if you think this is a mistake.",
    };
  }
  return {
    title: "This invite link is invalid or has expired",
    body: "Please ask your account admin to resend your invite.",
  };
}

function formatInviteError(message) {
  if (typeof message === "string" && message.trim()) return message;
  if (message && typeof message === "object") {
    const first = Object.values(message).find(
      (value) => typeof value === "string" && value.trim(),
    );
    if (first) return first;
  }
  return "Something went wrong. Please try again.";
}

const PASSWORD_HINTS = [
  { id: "length", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { id: "lower", label: "1 lowercase character", test: (p) => /[a-z]/.test(p) },
  { id: "number", label: "1 number", test: (p) => /\d/.test(p) },
  {
    id: "symbol",
    label: "1 symbol or uppercase character",
    test: (p) => /[A-Z]/.test(p) || /[\x20-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/.test(p),
  },
];

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [view, setView] = useState(token ? "form" : "dead");
  const [deadInfo, setDeadInfo] = useState(
    token
      ? null
      : {
          title: "This invite link is missing a token",
          body: "Please use the link from your invite email, or ask your admin to resend it.",
        },
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (password.length < 10) {
      setFormError("Password must be at least 10 characters.");
      return;
    }
    if (!PASSWORD_HINTS.every((hint) => hint.test(password))) {
      setFormError(
        "Your password must be at least 10 characters long and include at least one lowercase letter, one number, and either one uppercase letter or one special character.",
      );
      return;
    }
    if (password !== confirmPassword) {
      setFormError("Password and confirm password fields do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await clientTeamAcceptInvite({
        token,
        password,
        confirmPassword,
      });
      const data = res?.data?.data;

      if (res?.data?.success) {
        // Replace any leftover inviter session. App-wide `role` is "client";
        // the team permission (Admin/Manager/Member) lives on the JWT.
        localStorage.setItem("token", data?.token);
        localStorage.setItem("role", "client");
        localStorage.setItem("clientId", data?.clientId);
        localStorage.setItem("firstName", data?.firstName);
        if (data?.email) {
          localStorage.setItem("email", String(data.email).toLowerCase());
        }

        setView("success");
        setTimeout(() => navigate("/client-activity-summary"), 1500);
      }
    } catch (err) {
      const message = getTeamApiError(err);
      if (typeof message === "string" && DEAD_LINK_MESSAGES.has(message)) {
        setDeadInfo(deadStateCopy(message));
        setView("dead");
        return;
      }
      setFormError(formatInviteError(message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.acceptInviteWrap}>
      <div className={styles.shell}>
        <div className={styles.logoRow}>
          <span className={styles.logoText}>
            <Logo color="#030659" />
          </span>
        </div>

        <div className={styles.card}>
          {view === "dead" && (
            <div className={styles.centerState}>
              <div className={styles.deadIcon}>
                <IconLinkOff size={26} />
              </div>
              <h1 className={styles.title}>{deadInfo.title}</h1>
              <p className={styles.subtext}>{deadInfo.body}</p>
            </div>
          )}

          {view === "form" && (
            <>
              <div className={styles.cardIcon}>
                <IconUserPlus size={24} />
              </div>
              <h1 className={styles.title}>You're invited to Awasource</h1>
              <p className={styles.subtext}>
                Set a password to activate your account and get started.
              </p>

              {formError && (
                <div className={styles.alert + " " + styles.alertError}>
                  <IconAlertCircle size={17} />
                  <span>{formError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="password">
                    Password
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      className={styles.formInput}
                      placeholder="At least 10 characters"
                      autoComplete="new-password"
                      required
                      minLength={10}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.toggleVisibility}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword((s) => !s)}
                    >
                      {showPassword ? (
                        <IconEyeOff size={17} />
                      ) : (
                        <IconEye size={17} />
                      )}
                    </button>
                  </div>
                  <div className={styles.pwRequirements}>
                    {PASSWORD_HINTS.map((hint) => (
                      <div
                        key={hint.id}
                        className={`${styles.pwReqItem} ${
                          hint.test(password) ? styles.pwReqItemMet : ""
                        }`}
                      >
                        <IconCheck size={15} /> {hint.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      type={showConfirm ? "text" : "password"}
                      id="confirmPassword"
                      className={styles.formInput}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.toggleVisibility}
                      aria-label={
                        showConfirm ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowConfirm((s) => !s)}
                    >
                      {showConfirm ? (
                        <IconEyeOff size={17} />
                      ) : (
                        <IconEye size={17} />
                      )}
                    </button>
                  </div>
                  <div className={styles.formHint}>Both fields must match.</div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className={styles.submitBtn}
                  loading={submitting}
                >
                  {submitting
                    ? "Setting up your account…"
                    : "Accept Invite & Continue"}
                </Button>
              </form>
            </>
          )}

          {view === "success" && (
            <div className={styles.centerState}>
              <div className={styles.successIcon}>
                <IconCheck size={28} />
              </div>
              <h1 className={styles.title}>You're all set</h1>
              <p className={styles.subtext} style={{ marginBottom: 0 }}>
                Your account is active. Taking you to your dashboard…
              </p>
            </div>
          )}
        </div>

        {view !== "success" && (
          <div className={styles.footerLink}>
            Already have an account? <Link to="/login">Log in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
